import { GroupedFieldItemProps } from './types';

/**
 * Default grouped-field renderer: shows the group name + count, then each
 * subfield change as "Subfield: old → new" in an indented list.
 *
 * Domains needing a bespoke grouped layout (e.g. project USER_RATES) override
 * this via `strategy.renderGrouped`.
 */
export const GroupedFieldItem = ({ field, allFields, strategy }: GroupedFieldItemProps) => {
  const fieldName = field.fieldLocalizedName || strategy.getLocalizedFieldName(field.field);
  const count = field.groupCount || 0;

  return (
    <div className="w-full">
      <div className="flex w-full flex-col">
        <p className="text-sm font-medium leading-5">
          {fieldName}{' '}
          <span className="text-muted-foreground">{strategy.strings.changesCount(count)}</span>:
        </p>
        <div className="relative mt-2 flex flex-col gap-2 border-l-2 border-border pl-4">
          {field.subfieldChanges?.map((subfield, index) => {
            const subfieldName =
              subfield.fieldLocalizedName || strategy.getLocalizedFieldName(subfield.field);

            const formattedOldValue = strategy.formatFieldValue(subfield.from, subfield.field);
            const formattedNewValue = strategy.formatFieldValue(subfield.to, subfield.field);

            const renderValue = (value: string) =>
              strategy.renderValue(value, subfield.field, allFields);

            const onlyNewValue = !formattedOldValue && formattedNewValue;

            return (
              <div key={`${subfield.field}-${index}`} className="text-sm leading-5">
                <span className="font-medium">{subfieldName}:</span>
                {onlyNewValue ? (
                  <span> {renderValue(formattedNewValue)}</span>
                ) : (
                  <>
                    {formattedOldValue && (
                      <span className="text-muted-foreground line-through">
                        {' '}
                        {renderValue(formattedOldValue)}
                      </span>
                    )}
                    {formattedNewValue && (
                      <span>
                        {' '}
                        {strategy.strings.arrow} {renderValue(formattedNewValue)}
                      </span>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
